import { useEffect, useRef, useState } from 'react';
import adminAxios from '../lib/adminAxios';
import { Loader2, Check, AlertCircle, X, Trash2, RotateCcw, Sparkles } from 'lucide-react';
import { SETS, getProductImage } from '../data/products';
import useProducts, { invalidateProducts } from '../hooks/useProducts';
import { invalidateProductImages } from '../hooks/useProductImages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  'Wallet', 'Card holder', 'Business bag', 'Travel wallet',
  'Passport holder', 'Toiletry', 'Accessory', 'Other',
];

// Edit modal — pre-fills with the product's current data and PUTs to
// /api/admin/products/{code}. Works for both base (writes override) and
// custom (updates row) products.

export const EditProductDialog = ({ product, isCustom, hasOverride, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    name: product.name || '',
    category: product.category || '',
    dimensions: product.dimensions || '',
    leather: product.leather || '',
    description: product.description || '',
    price: product.price ?? '',
    currency: product.currency || 'gbp',
    features: product.features?.length ? [...product.features] : [''],
    colors: product.colors?.length
      ? product.colors.map((c) => ({ label: c.label || '', hex: c.hex || '#1a1a1a', ref: c.ref || '' }))
      : [{ label: '', hex: '#1a1a1a', ref: '' }],
    set_slugs: Array.isArray(product.set_slugs) ? [...product.set_slugs] : [],
  }));
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [busy, setBusy]           = useState(false);
  const [result, setResult]       = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [delBusy, setDelBusy]     = useState(false);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [aiError, setAiError]        = useState('');
  const stopTypingRef                = useRef(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(f ? URL.createObjectURL(f) : null);
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const setFeature = (i, v) => {
    const next = [...form.features]; next[i] = v;
    setForm({ ...form, features: next });
  };
  const addFeature = () => setForm({ ...form, features: [...form.features, ''] });
  const removeFeature = (i) => setForm({
    ...form, features: form.features.filter((_, idx) => idx !== i),
  });

  const setColor = (i, k, v) => {
    const next = [...form.colors]; next[i] = { ...next[i], [k]: v };
    setForm({ ...form, colors: next });
  };
  const addColor = () => setForm({
    ...form, colors: [...form.colors, { label: '', hex: '#1a1a1a', ref: '' }],
  });
  const removeColor = (i) => setForm({
    ...form, colors: form.colors.filter((_, idx) => idx !== i),
  });

  const toggleSet = (slug) => setForm((f) => ({
    ...f,
    set_slugs: f.set_slugs.includes(slug)
      ? f.set_slugs.filter((s) => s !== slug)
      : [...f.set_slugs, slug],
  }));

  const generateDescription = async () => {
    setAiError('');
    setAiStreaming(true);
    if (stopTypingRef.current) stopTypingRef.current();
    setForm((f) => ({ ...f, description: '' }));
    try {
      const { data } = await adminAxios.post(`${API}/admin/ai/generate-description`, {
        name:       form.name,
        category:   form.category,
        leather:    form.leather,
        dimensions: form.dimensions,
        features:   form.features.filter(Boolean),
      });
      // Typing animation — reveal text character by character
      let i = 0;
      const text = data.text || '';
      const id = setInterval(() => {
        i++;
        setForm((f) => ({ ...f, description: text.slice(0, i) }));
        if (i >= text.length) { clearInterval(id); setAiStreaming(false); }
      }, 9);
      stopTypingRef.current = () => clearInterval(id);
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message || 'Generation failed.');
      setAiStreaming(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);

    const features = form.features.map((s) => s.trim()).filter(Boolean);
    const colors = form.colors
      .filter((c) => c.label.trim() && c.hex.trim())
      .map((c) => ({ label: c.label.trim(), hex: c.hex.trim(), ref: c.ref.trim() || undefined }));

    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('category', form.category);
    fd.append('dimensions', form.dimensions);
    fd.append('leather', form.leather);
    fd.append('description', form.description);
    fd.append('features', JSON.stringify(features));
    fd.append('colors', JSON.stringify(colors));
    fd.append('set_slugs', JSON.stringify(form.set_slugs));
    if (form.price !== '' && form.price !== null) fd.append('price', String(form.price));
    if (form.currency) fd.append('currency', form.currency);
    if (file) fd.append('image', file);

    try {
      const { data } = await adminAxios.put(
        `${API}/admin/products/${encodeURIComponent(product.code)}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult({ ok: true, msg: `Saved (${data.kind}).` });
      invalidateProducts();
      invalidateProductImages?.();
      onSaved?.();
    } catch (err) {
      setResult({ ok: false, msg: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const resetToOriginal = async () => {
    if (!confirm(`Reset ${product.code} to its original house data? Any edits will be discarded.`)) return;
    setResetBusy(true);
    try {
      await adminAxios.delete(`${API}/admin/products/${encodeURIComponent(product.code)}`);
      invalidateProducts();
      onSaved?.();
      onClose?.();
    } catch (err) {
      setResult({ ok: false, msg: err?.response?.data?.detail || err.message });
    } finally {
      setResetBusy(false);
    }
  };

  const removeProduct = async () => {
    if (!confirm(`Remove ${product.code} (${product.name}) from the catalogue? This deletes any uploaded photos for this code.`)) return;
    setDelBusy(true);
    try {
      await adminAxios.delete(`${API}/admin/products/${encodeURIComponent(product.code)}`);
      invalidateProducts();
      invalidateProductImages?.();
      onSaved?.();
      onClose?.();
    } catch (err) {
      setResult({ ok: false, msg: err?.response?.data?.detail || err.message });
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] bg-mb/95 flex items-start md:items-center justify-center overflow-y-auto py-10 px-4"
      data-testid="edit-product-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-mb-surface hairline border-gold p-6 md:p-10 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="eyebrow text-gold">Editing</div>
            <h3 className="mt-2 font-display text-3xl text-mb">
              {product.code} <span className="text-mb-muted italic">· {product.name}</span>
            </h3>
            <div className="mt-2 eyebrow-sm text-mb-muted">
              {isCustom ? 'Custom piece' : hasOverride ? 'Base piece (currently edited)' : 'Base piece'}
            </div>
          </div>
          <button
            className="text-mb hover:text-gold p-2"
            onClick={onClose}
            data-testid="edit-close"
          >
            <X size={18} strokeWidth={1.25} />
          </button>
        </div>

        <form onSubmit={submit} data-testid="edit-product-form" className="mt-8">
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
            <div className="space-y-6">
              <Field label="Name" required value={form.name} onChange={update('name')} testid="edit-name" />
              <div>
                <label className="mb-label">Category</label>
                <select
                  data-testid="edit-category"
                  required
                  className="mb-input"
                  value={form.category}
                  onChange={update('category')}
                  style={{ appearance: 'none' }}
                >
                  <option value="" style={{ background: '#2a0d1a', color: '#F0EDE5' }}>Select</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c} style={{ background: '#2a0d1a', color: '#F0EDE5' }}>{c}</option>
                  ))}
                </select>
              </div>
              <Field label="Dimensions" required value={form.dimensions} onChange={update('dimensions')} testid="edit-dimensions" />
              <Field label="Leather" required value={form.leather} onChange={update('leather')} testid="edit-leather" />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="mb-label">Description</label>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 eyebrow-sm text-gold hover:text-gold/70 transition-colors disabled:opacity-40"
                    onClick={generateDescription}
                    disabled={aiStreaming}
                    data-testid="edit-ai-generate-desc"
                  >
                    {aiStreaming
                      ? <><Loader2 size={11} className="animate-spin" />Writing…</>
                      : <><Sparkles size={11} />Generate with Claude</>}
                  </button>
                </div>
                <textarea
                  rows={4}
                  className="mb-input"
                  value={form.description}
                  onChange={update('description')}
                  placeholder={aiStreaming ? '— Claude is writing —' : ''}
                  data-testid="edit-description"
                />
                {aiError && (
                  <p className="mt-1 text-[11px] text-red-400 flex items-center gap-1">
                    <AlertCircle size={10} /> {aiError}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-label">Add to set(s)</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SETS.map((s) => {
                    const on = form.set_slugs.includes(s.slug);
                    return (
                      <button
                        type="button"
                        key={s.slug}
                        onClick={() => toggleSet(s.slug)}
                        data-testid={`edit-set-${s.slug}`}
                        className={`feature-badge ${on ? '' : 'feature-badge--muted'}`}
                      >
                        {on ? '●' : '○'} {s.name}
                      </button>
                    );
                  })}
                </div>
                {!isCustom && (
                  <p className="mt-2 eyebrow-sm text-mb-muted">
                    Note: base products&apos; set membership is curated by the house — toggling here is honoured for this piece&apos;s listings, but the original set data still includes it.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="mb-label">Replace photo (optional)</label>
                <div className="mt-2 grid grid-cols-[110px_1fr] gap-3 items-start">
                  <div className="aspect-[4/5] hairline border-subtle bg-mb overflow-hidden flex items-center justify-center">
                    {filePreview ? (
                      <img src={filePreview} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <img
                        src={getProductImage(product.code)}
                        alt={product.code}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onFile}
                      data-testid="edit-image"
                      className="block w-full text-[12px] text-mb-muted
                                 file:mr-3 file:py-2 file:px-3 file:hairline
                                 file:border-gold file:bg-transparent file:text-gold
                                 file:eyebrow-sm file:cursor-pointer"
                    />
                    <p className="mt-2 eyebrow-sm text-mb-muted">Replaces gv-{product.code.split(' ')[1]}.jpg.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-label">Features</label>
                <div className="space-y-2">
                  {form.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className="mb-input"
                        value={f}
                        onChange={(e) => setFeature(i, e.target.value)}
                        placeholder="e.g. RFID + NFC"
                        data-testid={`edit-feature-${i}`}
                      />
                      <button type="button" onClick={() => removeFeature(i)} className="text-mb-muted hover:text-gold p-1">
                        <X size={14} strokeWidth={1.25} />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addFeature} className="mt-2 eyebrow text-gold">+ Add feature</button>
              </div>

              <div>
                <label className="mb-label">Colors / finishes</label>
                <div className="space-y-2">
                  {form.colors.map((c, i) => (
                    <div key={i} className="grid grid-cols-[40px_1fr_1fr_28px] gap-2 items-center">
                      <input
                        type="color"
                        value={c.hex}
                        onChange={(e) => setColor(i, 'hex', e.target.value)}
                        className="w-10 h-10 hairline border-subtle bg-transparent cursor-pointer"
                        data-testid={`edit-color-hex-${i}`}
                      />
                      <input
                        className="mb-input"
                        placeholder="Label"
                        value={c.label}
                        onChange={(e) => setColor(i, 'label', e.target.value)}
                        data-testid={`edit-color-label-${i}`}
                      />
                      <input
                        className="mb-input"
                        placeholder="Ref"
                        value={c.ref}
                        onChange={(e) => setColor(i, 'ref', e.target.value)}
                        data-testid={`edit-color-ref-${i}`}
                      />
                      <button type="button" onClick={() => removeColor(i)} className="text-mb-muted hover:text-gold p-1">
                        <X size={14} strokeWidth={1.25} />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addColor} className="mt-2 eyebrow text-gold">+ Add finish</button>
              </div>
            </div>
          </div>

          <div className="mt-8 hairline-t border-subtle pt-5 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              className="btn-gold disabled:opacity-50"
              disabled={busy}
              data-testid="edit-submit"
            >
              {busy ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : 'Save changes'}
            </button>

            {!isCustom && hasOverride && (
              <button
                type="button"
                className="btn-outline-gold disabled:opacity-50"
                onClick={resetToOriginal}
                disabled={resetBusy}
                data-testid="edit-reset"
              >
                {resetBusy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} strokeWidth={1.25} />}
                Reset to original
              </button>
            )}

            {isCustom && (
              <button
                type="button"
                className="eyebrow text-mb-muted hover:text-gold inline-flex items-center gap-2 ml-auto disabled:opacity-50"
                onClick={removeProduct}
                disabled={delBusy}
                data-testid="edit-delete"
              >
                {delBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} strokeWidth={1.25} />}
                Delete piece
              </button>
            )}

            {result && (
              <div
                className={`hairline px-3 py-2 ${result.ok ? 'border-gold' : 'border-subtle'}`}
                data-testid="edit-result"
              >
                <div className={`flex items-center gap-2 eyebrow-sm ${result.ok ? 'text-gold' : 'text-mb-muted'}`}>
                  {result.ok ? <Check size={12} /> : <AlertCircle size={12} />}
                  {result.msg}
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid, required }) => (
  <div>
    <label className="mb-label">{label}{required && <span className="text-gold ml-1">*</span>}</label>
    <input
      type={type}
      className="mb-input"
      value={value}
      onChange={onChange}
      data-testid={testid}
      required={required}
    />
  </div>
);

export default EditProductDialog;
